import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isSupported, play, playSteps, stop } from '@/audio/player'
import { binnenTimeline } from '@/core/binnenSignale'
import { binnenSignale, buoys, lightStates, signals } from '@/core/data'
import { questionFor, rng } from '@/core/quiz'
import { loadProgress, naechsteAusKorb, nextCardId, recordAnswer, saveProgress, type KorbId, type Progress } from '@/core/progress'
import { gradeFor } from '@/core/srs'
import { citation, resolveCite, ruleCitation, ruleText } from '@/core/sources'
import { cardFace } from '@/core/cards'
import { MitBegriffen } from './MitBegriffen'
import { useSettings } from './settings'
import { subjectOfCard, type Subject } from './Viewer'

export interface QuizProps {
  /** Der Korb, aus dem geübt wird. Die App hält ihn, weil er über der Szene steht. */
  korb?: KorbId
  /** Zählt hoch, wenn die App einen anderen Korb gewählt hat. */
  korbWechsel?: number
  onSubject: (subject: Subject) => void
  /** Ruft die App, wenn die nächste Frage kommt: danach greift die Kaufschranke. */
  onAdvance?: () => void
}

/**
 * Quiz: die Szene zeigt das Bild, die Frage nennt es nicht. Nach der Antwort kommt die
 * Auflösung mit dem Regeltext, denn ein Quiz ohne Begründung ist Raten mit Punkten.
 */
export function Quiz({ onSubject, onAdvance, korb, korbWechsel = 0 }: QuizProps) {
  const { t, pick, lang, settings } = useSettings()
  const bereich = settings.bereich
  const [progress, setProgress] = useState<Progress>(() => loadProgress())
  const [cardId, setCardId] = useState<string | undefined>(() => nextCardId(loadProgress(), undefined, bereich))
  const [chosen, setChosen] = useState<number | undefined>()
  const started = useRef(Date.now())

  /** Wechselt der Korb, zieht das Quiz seine nächste Karte aus genau diesem. */
  useEffect(() => {
    if (korbWechsel === 0) return
    setChosen(undefined)
    const naechste = korb ? naechsteAusKorb(loadProgress(), korb, bereich) : nextCardId(loadProgress(), undefined, bereich)
    if (naechste) setCardId(naechste)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [korbWechsel])

  const question = useMemo(() => {
    if (!cardId) return undefined
    return questionFor(cardId, rng(progress.seed + cardId.length + (progress.cards[cardId]?.attempts ?? 0)))
  }, [cardId, progress])

  useEffect(() => {
    if (question) onSubject(subjectOfCard(question.cardId))
    started.current = Date.now()
  }, [question, onSubject])

  const answer = useCallback(
    (index: number) => {
      if (!question || chosen !== undefined) return
      setChosen(index)
      const seconds = (Date.now() - started.current) / 1000
      const next = recordAnswer(progress, question.cardId, gradeFor(index === question.correct, seconds))
      setProgress(next)
      saveProgress(next)
    },
    [question, chosen, progress],
  )

  const advance = useCallback(() => {
    stop()
    setChosen(undefined)
    // Bleibt im Korb, wenn einer gewählt ist: wer seine Fehler übt, will die Fehler.
    setCardId(
      (korb ? naechsteAusKorb(progress, korb, bereich) : undefined) ??
        nextCardId(progress, undefined, bereich),
    )
    onAdvance?.()
  }, [progress, onAdvance, korb, bereich])

  if (!question) return <p className="detail__text">{t('quiz.empty')}</p>

  const correct = chosen === question.correct
  const state = question.kind === 'light-state' ? lightStates.find((s) => s.id === question.subject) : undefined
  const buoy = question.kind === 'buoy' ? buoys.find((b) => b.id === question.subject) : undefined
  const signal = question.kind === 'signal' ? signals.find((s) => s.id === question.subject) : undefined
  const binnenZeichen =
    question.kind === 'binnen-signal' ? binnenSignale.find((z) => z.id === question.subject) : undefined

  return (
    <section className="quiz" data-testid="quiz" data-bereich={bereich}>
      <h2 className="quiz__prompt">{pick(question.prompt)}</h2>
      {signal && (
        <div className="signal__controls" data-testid="quiz-signal">
          <button
            type="button"
            className="signal__play"
            data-testid="quiz-signal-play"
            disabled={!isSupported()}
            onClick={() => void play(signal)}
          >
            {t('quiz.signal.play')}
          </button>
          <span className="signal__legend">
            {signal.repeatS > 0 ? t('quiz.signal.repeat') : t('quiz.signal.once')}
          </span>
        </div>
      )}
      {binnenZeichen && binnenZeichen.pattern.length > 0 && (
        <div className="signal__controls" data-testid="quiz-signal">
          <button
            type="button"
            className="signal__play"
            data-testid="quiz-signal-play"
            disabled={!isSupported()}
            onClick={() => void playSteps(binnenTimeline(binnenZeichen))}
          >
            {t('quiz.signal.play')}
          </button>
        </div>
      )}
      <div className="quiz__options">
        {question.options.map((option, index) => (
          <button
            key={option.id}
            type="button"
            className={`quiz__option${
              chosen === undefined
                ? ''
                : index === question.correct
                  ? ' quiz__option--right'
                  : index === chosen
                    ? ' quiz__option--wrong'
                    : ''
            }`}
            data-option={option.id}
            disabled={chosen !== undefined}
            onClick={() => answer(index)}
          >
            {pick(option.label)}
          </button>
        ))}
      </div>

      {chosen !== undefined && (
        <div className="quiz__result" data-testid="quiz-result">
          <p className="quiz__verdict" data-correct={correct ? 'ja' : 'nein'}>
            {correct ? t('quiz.right') : t('quiz.wrong', { answer: pick(question.options[question.correct]!.label) })}
          </p>
          {/* Die Auflösung kommt aus derselben Karte wie im Lernmodus. Vorher hing sie an
              drei Zweigen für Seezustand, Seezeichen und Signal, und eine Binnenfrage
              traf keinen davon: dort stand nur "Richtig." und sonst nichts. Wer falsch
              lag, erfuhr nicht, warum. */}
          {question.kind === 'binnen-signal' && (
            <>
              <MitBegriffen text={cardFace(question.cardId, lang).explanation} className="detail__text" lang="de" />
              <h3 className="detail__legend" data-testid="quiz-fundstelle">
                {cardFace(question.cardId, lang).citation}
              </h3>
            </>
          )}
          {question.kind === 'binnen-state' && (
            <>
              <MitBegriffen text={cardFace(question.cardId, lang).explanation} className="detail__text" lang="de" />
              <h3 className="detail__legend">{cardFace(question.cardId, lang).citation}</h3>
              <ul className="detail__quotes">
                {cardFace(question.cardId, lang).quotes.slice(0, 3).map((quote, i) => (
                  <li key={i} lang="de">
                    {quote}
                  </li>
                ))}
              </ul>
            </>
          )}
          {state && (
            <>
              <MitBegriffen text={pick(state.explanation)} className="detail__text" />
              <h3 className="detail__legend">
                {ruleCitation(state.rule, state.cite)}
                {state.cfr ? ` · ${citation(state.cfr)}` : ''}
              </h3>
              <ul className="detail__quotes">
                {state.cite.slice(0, 3).map((entry) => (
                  <li key={entry}>{ruleText(resolveCite(state.rule, entry, state.scope).paragraph)}</li>
                ))}
              </ul>
            </>
          )}
          {signal && (
            <>
              <p className="detail__text">
                <strong>{pick(signal.name)}: </strong>
                {pick(signal.explanation)}
              </p>
              <h3 className="detail__legend">{ruleCitation(signal.rule, signal.cite)}</h3>
              <ul className="detail__quotes">
                {signal.cite.map((entry) => (
                  <li key={entry}>{ruleText(resolveCite(signal.rule, entry, signal.scope).paragraph)}</li>
                ))}
              </ul>
            </>
          )}
          {buoy && (
            <>
              <p className="detail__text">{pick(buoy.meaning)}</p>
              <p className="detail__text">{pick(buoy.explanation)}</p>
              {buoy.character && <p className="detail__character">{buoy.character}</p>}
            </>
          )}
          <button type="button" className="toggle" data-testid="quiz-next" onClick={advance}>
            {t('quiz.next')}
          </button>
        </div>
      )}
    </section>
  )
}
